import type { SeatInfo } from "@app/protocol";
import { useEffect, useRef } from "react";
import { LATE_SHARE } from "../audio/countdown";
import { useT } from "../i18n/context";
import { useStore } from "../store";
import { Avatar } from "./Avatar";
import { CardBack } from "./Card";
import { Icon } from "./icons";

export type PodBadge = "away" | "bot" | "owner" | "auto";

/** When the acting seat's time runs out, on this client's clock, and the whole budget in ms. */
export interface TurnClock {
  endsAt: number;
  budget: number;
}

const BADGE: Record<PodBadge, { bg: string; icon: "wifiOff" | "bot" | "crown" }> = {
  away: { bg: "bg-danger", icon: "wifiOff" },
  bot: { bg: "bg-slate", icon: "bot" },
  owner: { bg: "bg-crown", icon: "crown" },
  // The table plays this seat until its player takes over (decision 173).
  auto: { bg: "bg-gold", icon: "bot" },
};

/** The empty seat (handoff 3b): a dashed circle with a plus; a button when you may sit. */
export function EmptySeat({
  size,
  label,
  onSit,
}: {
  size: number;
  label: string;
  onSit?: (() => void) | undefined;
}) {
  const inner = (
    <span className="flex h-full w-full items-center justify-center rounded-full border-2 border-dashed border-ink/35 bg-ground/25 transition-colors duration-150 group-hover:border-ink/70 group-hover:bg-ground/40">
      <Icon name="plus" size={Math.round(size * 0.23)} strokeWidth={2} className="text-ink/70" />
    </span>
  );
  const frame = "group block h-full w-full rounded-full bg-ink/14 p-[3px]";
  return onSit ? (
    <button type="button" aria-label={label} className={frame} onClick={onSit}>
      {inner}
    </button>
  ) : (
    <span aria-hidden="true" className={frame}>
      {inner}
    </span>
  );
}

/**
 * The avatar with the turn clock over it: the time left as a slice opening
 * clockwise from 12 (handoff 3a), redrawn every frame so it runs without steps,
 * red in the last quarter (owner, 11 Sept). Only this element repaints, not the table.
 */
export function Face({
  seat,
  size,
  clock,
}: {
  seat: SeatInfo;
  size: number;
  clock: TurnClock | null;
}) {
  const slice = useRef<HTMLSpanElement>(null);
  const speaking = useStore((s) => s.speaking.includes(seat.userId));
  const endsAt = clock?.endsAt ?? null;
  const budget = clock?.budget ?? 1;
  useEffect(() => {
    const el = slice.current;
    if (endsAt === null || el === null) return;
    let frame = 0;
    const draw = () => {
      const left = Math.min(1, Math.max(0, endsAt - Date.now()) / budget);
      const tone = left <= LATE_SHARE ? "var(--p-danger)" : "var(--p-success)";
      const gone = (1 - left) * 100;
      el.style.background = `conic-gradient(from 0deg, transparent 0 ${gone}%, color-mix(in srgb, ${tone} 50%, transparent) ${gone}% 100%)`;
      if (left > 0) frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [endsAt, budget]);
  return (
    <span
      className={`relative block overflow-hidden rounded-full shadow-[0_20px_40px_-16px_rgb(0_0_0/0.9)] ${speaking ? "outline outline-[3px] outline-offset-2 outline-success" : ""}`}
      style={{ width: size, height: size }}
    >
      <Avatar member={seat} size={size} />
      {clock ? <span ref={slice} aria-hidden="true" className="absolute inset-0" /> : null}
    </span>
  );
}

/** A badge on the circle's edge at two o'clock: away, bot or owner. */
export function Badge({ badge, size }: { badge: PodBadge; size: number }) {
  const r = size / 2;
  const d = Math.max(20, Math.round(size * 0.2));
  return (
    <span
      aria-hidden="true"
      className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-ink shadow-[0_4px_10px_rgb(0_0_0/0.5)] ${BADGE[badge].bg}`}
      style={{
        left: r + r * Math.cos(Math.PI / 6),
        top: r - r * Math.sin(Math.PI / 6),
        width: d,
        height: d,
      }}
    >
      <Icon name={BADGE[badge].icon} size={Math.round(d * 0.53)} />
    </span>
  );
}

/** At most this many backs are drawn; Pişti's hand is four, and a long one would ring the avatar. */
const FAN = ["a", "b", "c", "d", "e", "f"];

/**
 * The cards this seat still holds, as little backs behind the avatar, going one by one as they
 * are played (owner, 20 Sept 2026). Only where a hand is short enough to count at a glance
 * (`GameFacts.handBacks`): in a trick game all four hands hold the same number and this says
 * nothing.
 */
export function HandBacks({ count, size }: { count: number; size: number }) {
  const { t } = useT();
  const w = Math.round(size * 0.32);
  const h = Math.round(w * 1.42);
  const step = w * 0.6;
  const shown = FAN.slice(0, count);
  return (
    <span
      role="img"
      aria-label={t("table.seat.hand", { count })}
      className="pointer-events-none absolute left-1/2 top-0"
      style={{ translate: `0 ${-h * 0.5}px` }}
    >
      {shown.map((id, i) => (
        <span
          key={id}
          className="absolute block transition-[translate,rotate] duration-200"
          // The size is set here too: in a box of its own width the picture would be squeezed to
          // nothing, because the reset caps every image at its parent's width (20 Sept 2026).
          style={{
            width: w,
            height: h,
            translate: `${(i - (shown.length - 1) / 2) * step - w / 2}px 0`,
            rotate: `${(i - (shown.length - 1) / 2) * 11}deg`,
          }}
        >
          <CardBack width={w} height={h} />
        </span>
      ))}
    </span>
  );
}
